import { useState, useEffect, useMemo } from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { FixedSkillCategory } from './components/FixedSkillCategory'
import { LanguageSkillsSection } from './components/LanguageSkillsSection'
import type { SkillsEditorProps, ExpandedState } from './types'
import type { SkillCategory, SkillFieldMapping } from '../../types/skills'
import { getSkillsByCategory } from '../../types/skills'

export function SkillsEditor({
  prSheetId,
  fixedSkillRatings,
  onFixedSkillRatingChange,
  onFixedSkillDescriptionChange,
  languageSkills,
  onLanguageSkillsChange,
  onLanguageSkillDelete,
}: SkillsEditorProps) {
  // レーティングが設定されている固定スキルがあるかチェック
  const hasRatedSkills = useMemo(() => {
    return (category: SkillCategory): boolean => {
      const skills = getSkillsByCategory(category)
      return skills.some((skill: SkillFieldMapping) => {
        const skillData = fixedSkillRatings[skill.careerField]
        return skillData && skillData.rating !== null
      })
    }
  }, [fixedSkillRatings])

  // 各カテゴリーの展開状態
  // レーティングが設定されているカテゴリーはデフォルトで展開
  const [expanded, setExpanded] = useState<ExpandedState>(() => {
    const initialState: ExpandedState = {
      engineering: false,
      game: false,
      creative: false,
      language: false,
    }

    initialState.engineering = hasRatedSkills('engineering')
    initialState.game = hasRatedSkills('game')
    initialState.creative = hasRatedSkills('creative')
    initialState.language = languageSkills.length > 0

    return initialState
  })

  // レーティングが変更されたら、そのカテゴリーを展開（初回のみ）
  useEffect(() => {
    const categories: SkillCategory[] = ['engineering', 'game', 'creative']
    categories.forEach((category) => {
      const skills = getSkillsByCategory(category)
      const hasRated = skills.some((skill: SkillFieldMapping) => {
        const skillData = fixedSkillRatings[skill.careerField]
        return skillData && skillData.rating !== null
      })
      // レーティングがある場合のみ展開（ユーザーが手動で閉じた状態は保持）
      if (hasRated && !expanded[category]) {
        setExpanded((prev) => ({ ...prev, [category]: true }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedSkillRatings])

  // 言語スキルが追加されたら展開
  useEffect(() => {
    if (languageSkills.length > 0 && !expanded.language) {
      setExpanded((prev) => ({ ...prev, language: true }))
    }
  }, [languageSkills.length, expanded.language])

  const handleExpandedChange = (category: SkillCategory, isExpanded: boolean) => {
    setExpanded((prev) => ({ ...prev, [category]: isExpanded }))
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        🛠 スキル
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FixedSkillCategory
          category="engineering"
          expanded={expanded.engineering}
          onExpandedChange={(isExpanded) => handleExpandedChange('engineering', isExpanded)}
          ratings={fixedSkillRatings}
          onRatingChange={onFixedSkillRatingChange}
          onDescriptionChange={onFixedSkillDescriptionChange}
        />
        <FixedSkillCategory
          category="game"
          expanded={expanded.game}
          onExpandedChange={(isExpanded) => handleExpandedChange('game', isExpanded)}
          ratings={fixedSkillRatings}
          onRatingChange={onFixedSkillRatingChange}
          onDescriptionChange={onFixedSkillDescriptionChange}
        />
        <FixedSkillCategory
          category="creative"
          expanded={expanded.creative}
          onExpandedChange={(isExpanded) => handleExpandedChange('creative', isExpanded)}
          ratings={fixedSkillRatings}
          onRatingChange={onFixedSkillRatingChange}
          onDescriptionChange={onFixedSkillDescriptionChange}
        />
        <LanguageSkillsSection
          expanded={expanded.language}
          onExpandedChange={(isExpanded) => handleExpandedChange('language', isExpanded)}
          skills={languageSkills}
          onChange={onLanguageSkillsChange}
          onDelete={onLanguageSkillDelete}
          prSheetId={prSheetId}
        />
      </Box>
    </Paper>
  )
}
